import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Loader2, ThumbsUp, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Kpi, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

const words = (s) => String(s || "").replace(/_/g, " ");
const count = (s) => String(s || "").split(/\s+/).filter(Boolean).length;
const iso = (local) => (local ? `${local}:00+00:00` : "");
const pretty = (s) => String(s || "").slice(0, 16).replace("T", " ");

const CHIP = {
  awaiting_support: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  support_threshold_met: "border-primary/30 bg-primary/10 text-primary",
  under_review: "border-primary/30 bg-primary/10 text-primary",
  more_information_required: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  rejected: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  withdrawn: "border-border bg-muted text-muted-foreground",
};

const F = ({ label, hint, children, className }) => (
  <div className={`space-y-1.5 ${className || ""}`}>
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] leading-relaxed text-muted-foreground">{hint}</p>}
  </div>
);

const BLANK = { position_ref: "", candidate_email: "", statement: "", biography: "",
  qualifications: "", experience: "", consent_to_stand: false, conflict_declared: false,
  conflict_note: "" };

export const Nominations = ({ electionRef, onChanged }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [window_, setWindow] = useState(null);
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const res = await api.get(`/elections/${electionRef}/nominations`);
    setData(res.data);
  }, [electionRef]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const run = async (key, fn, message) => {
    setBusy(key);
    try {
      await fn();
      toast.success(message);
      await load();
      setForm(null); setWindow(null); setReview(null);
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return <Panel title="Nominations" testId="panel-nominations-loading">
    <p className="text-[12.5px] text-muted-foreground">Opening the nominations book…</p></Panel>;

  const limit = data.statement_word_limit || 1000;
  const open = data.nominations_open;
  const w = data.window || {};

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="nom-kpi-total" label="Nominations received" icon={UserPlus}
             value={data.counters.total} sub={`${data.minimum_supporters} supporters needed each`} />
        <Kpi testId="nom-kpi-support" label="Still gathering support" accent="violet"
             icon={ThumbsUp} value={data.counters.awaiting_support}
             sub={`${data.counters.ready_for_review} ready for review`} />
        <Kpi testId="nom-kpi-approved" label="Approved candidates" accent="emerald"
             value={data.counters.approved} sub={`${data.counters.rejected} not accepted`} />
        <Kpi testId="nom-kpi-window" label="Nominations" accent="teal" icon={CalendarClock}
             value={open ? "Open" : "Closed"}
             sub={w.closes_at ? `${open ? "closes" : "closed"} ${pretty(w.closes_at)}`
               : "no window set"} />
      </div>

      <Panel title="The nomination window" testId="panel-nomination-window"
             kicker="Set by the serving Election Commission — nothing is accepted outside it"
             actions={data.you_are_on_commission && (
               <Button size="sm" className="h-8 rounded-full" data-testid="set-window-button"
                       onClick={() => setWindow({ opens_at: "", closes_at: "" })}>
                 {w.opens_at ? "Change the window" : "Set the window"}
               </Button>
             )}>
        <p className="text-[12.5px] text-muted-foreground" data-testid="nomination-window-text">
          {w.opens_at
            ? `Nominations run from ${pretty(w.opens_at)} to ${pretty(w.closes_at)}.`
            : "No window has been set yet."}
          {" "}
          {open ? "They are open now." : "They are not open at the moment."}
        </p>
        {!data.you_are_on_the_roll && (
          <p className="mt-2 text-[11.5px] text-muted-foreground" data-testid="not-on-roll">
            You are not on the frozen voter roll for this election, so you may neither stand nor
            support a nomination.
          </p>
        )}
        {data.you_are_on_the_roll && data.why_you_may_not_stand && (
          <p className="mt-2 text-[11.5px] text-amber-700 dark:text-amber-300"
             data-testid="why-you-may-not-stand">
            You may support others but not stand: {data.why_you_may_not_stand}.
          </p>
        )}
      </Panel>

      <Panel title="Nominations" testId="panel-nominations"
             kicker={`Every candidate gets the same ${limit} words`}
             actions={(
               <Button size="sm" data-testid="stand-button"
                       disabled={!open || !data.you_may_stand}
                       className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                       onClick={() => setForm({ ...BLANK,
                         position_ref: data.positions[0]?.position_ref || "" })}>
                 <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Stand for a post
               </Button>
             )}>
        {data.items.length === 0 ? (
          <p className="text-[12.5px] text-muted-foreground" data-testid="nominations-empty">
            Nobody has been nominated yet.
          </p>
        ) : (
          <div className="space-y-2.5" data-testid="nomination-list">
            {data.items.map((n) => (
              <div key={n.nomination_ref} data-testid={`nomination-${n.nomination_ref}`}
                   className="rounded-xl border border-border p-3.5">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-[13px] font-semibold text-foreground">
                      {n.candidate.name}
                      <span className="pl-2 text-[11.5px] font-normal text-muted-foreground">
                        for {n.position_title}
                      </span>
                    </p>
                    <p className="text-[10.5px] text-muted-foreground">
                      {n.nomination_ref} · {n.self_nominated ? "stood themselves"
                        : `nominated by ${n.nominated_by_name}`}
                      {" · "}{n.statement_word_limit}-word limit
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Chip className={CHIP[n.status]} testId={`nomination-status-${n.nomination_ref}`}>
                      {words(n.status)}
                    </Chip>
                    <span className="font-mono text-[11px] text-muted-foreground"
                          data-testid={`support-count-${n.nomination_ref}`}>
                      {n.support_count}/{n.minimum_supporters} supporters
                    </span>
                  </div>
                </div>

                <p className="mt-2 whitespace-pre-line text-[12px] leading-relaxed text-muted-foreground">
                  {n.statement}
                </p>

                {n.review?.decision && n.you_may_see_the_decision && (
                  <p className="mt-2 rounded-lg bg-muted px-3 py-2 text-[11.5px] text-foreground"
                     data-testid={`review-${n.nomination_ref}`}>
                    {words(n.review.decision)} by {n.review.by}
                    {n.review.reason ? ` — ${n.review.reason}` : ""}
                    {n.review.rule_reference ? ` (${n.review.rule_reference})` : ""}
                  </p>
                )}

                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  {["awaiting_support", "support_threshold_met"].includes(n.status) && (
                    <Button size="sm" variant="outline" className="h-8 rounded-full"
                            data-testid={`support-${n.nomination_ref}`}
                            disabled={!open || n.you_supported || n.you_are_the_candidate
                              || !data.you_are_on_the_roll || busy === n.nomination_ref}
                            onClick={() => run(n.nomination_ref,
                              () => api.post(`/elections/nominations/${n.nomination_ref}/support`),
                              `You are backing ${n.candidate.name}`)}>
                      <ThumbsUp className="mr-1.5 h-3.5 w-3.5" />
                      {n.you_supported ? "You back this" : "Support"}
                    </Button>
                  )}
                  {n.you_are_the_candidate
                    && !["withdrawn", "rejected"].includes(n.status) && (
                    <Button size="sm" variant="outline" className="h-8 rounded-full"
                            data-testid={`withdraw-${n.nomination_ref}`}
                            onClick={() => run(n.nomination_ref,
                              () => api.post(`/elections/nominations/${n.nomination_ref}/withdraw`),
                              "Your nomination has been withdrawn")}>
                      Withdraw
                    </Button>
                  )}
                  {data.you_are_on_commission
                    && !["withdrawn", "rejected", "approved"].includes(n.status) && (
                    <>
                      <Button size="sm" className="h-8 rounded-full"
                              data-testid={`approve-${n.nomination_ref}`}
                              disabled={n.status === "awaiting_support"}
                              onClick={() => run(n.nomination_ref,
                                () => api.post(
                                  `/elections/nominations/${n.nomination_ref}/review`,
                                  { decision: "approved" }),
                                `${n.candidate.name} is a confirmed candidate`)}>
                        Approve
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 rounded-full"
                              data-testid={`reject-${n.nomination_ref}`}
                              onClick={() => setReview({ ref: n.nomination_ref,
                                decision: "rejected", reason: "", rule_reference: "",
                                name: n.candidate.name })}>
                        Reject
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 rounded-full"
                              data-testid={`more-info-${n.nomination_ref}`}
                              onClick={() => setReview({ ref: n.nomination_ref,
                                decision: "more_information_required", reason: "",
                                rule_reference: "", name: n.candidate.name })}>
                        Ask for more
                      </Button>
                    </>
                  )}
                  {n.supporters.length > 0 && (
                    <span className="text-[10.5px] text-muted-foreground"
                          data-testid={`supporters-${n.nomination_ref}`}>
                      backed by {n.supporters.map((s) => s.name).join(", ")}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* the nomination window */}
      <Dialog open={Boolean(window_)} onOpenChange={(o) => !o && setWindow(null)}>
        <DialogContent className="max-w-lg" data-testid="window-dialog">
          <DialogHeader><DialogTitle>Set the nomination window</DialogTitle>
            <DialogDescription>
              Nothing is accepted before it opens or after it closes. Times are UTC.
            </DialogDescription></DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <F label="Opens">
              <Input type="datetime-local" data-testid="window-opens"
                     value={window_?.opens_at || ""}
                     onChange={(e) => setWindow({ ...window_, opens_at: e.target.value })} />
            </F>
            <F label="Closes">
              <Input type="datetime-local" data-testid="window-closes"
                     value={window_?.closes_at || ""}
                     onChange={(e) => setWindow({ ...window_, closes_at: e.target.value })} />
            </F>
          </div>
          <DialogFooter>
            <Button data-testid="window-save" disabled={busy === "window"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("window",
                      () => api.put(`/elections/${electionRef}/nomination-window`,
                        { opens_at: iso(window_.opens_at), closes_at: iso(window_.closes_at) }),
                      "The nomination window is set and the roll has been told")}>
              {busy === "window" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Set it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* stand for a post */}
      <Dialog open={Boolean(form)} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto"
                       data-testid="nomination-dialog">
          <DialogHeader><DialogTitle>Nomination</DialogTitle>
            <DialogDescription>
              Stand yourself, or nominate another member on the roll. Every candidate gets the
              same {limit} words.
            </DialogDescription></DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <F label="Post">
              <Select value={form?.position_ref}
                      onValueChange={(v) => setForm({ ...form, position_ref: v })}>
                <SelectTrigger data-testid="nomination-position"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {data.positions.map((p) => (
                    <SelectItem key={p.position_ref} value={p.position_ref}>
                      {p.title} ({p.seats} seat{p.seats > 1 ? "s" : ""})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            <F label="Candidate's email"
               hint="Leave it empty to stand yourself.">
              <Input data-testid="nomination-candidate" value={form?.candidate_email || ""}
                     onChange={(e) => setForm({ ...form, candidate_email: e.target.value })} />
            </F>
            <F label={`Statement — ${count(form?.statement)} of ${limit} words`}
               className="md:col-span-2"
               hint="This is what every voting member reads. Over the limit it will be refused.">
              <Textarea rows={7} data-testid="nomination-statement"
                        value={form?.statement || ""}
                        onChange={(e) => setForm({ ...form, statement: e.target.value })} />
            </F>
            <p className={`md:col-span-2 text-[11px] ${count(form?.statement) > limit
              ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}`}
               data-testid="statement-word-count">
              {count(form?.statement)} / {limit} words
              {count(form?.statement) > limit ? " — trim it before you send" : ""}
            </p>
            <F label="Biography" className="md:col-span-2">
              <Textarea rows={3} data-testid="nomination-biography"
                        value={form?.biography || ""}
                        onChange={(e) => setForm({ ...form, biography: e.target.value })} />
            </F>
            <F label="Qualifications">
              <Textarea rows={2} data-testid="nomination-qualifications"
                        value={form?.qualifications || ""}
                        onChange={(e) => setForm({ ...form, qualifications: e.target.value })} />
            </F>
            <F label="Experience">
              <Textarea rows={2} data-testid="nomination-experience"
                        value={form?.experience || ""}
                        onChange={(e) => setForm({ ...form, experience: e.target.value })} />
            </F>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <span className="text-[12px] text-foreground">Consent to stand</span>
              <Switch data-testid="nomination-consent" checked={Boolean(form?.consent_to_stand)}
                      onCheckedChange={(v) => setForm({ ...form, consent_to_stand: v })} />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <span className="text-[12px] text-foreground">Conflicts declared</span>
              <Switch data-testid="nomination-conflict" checked={Boolean(form?.conflict_declared)}
                      onCheckedChange={(v) => setForm({ ...form, conflict_declared: v })} />
            </div>
            <F label="Conflict note" className="md:col-span-2">
              <Textarea rows={2} data-testid="nomination-conflict-note"
                        value={form?.conflict_note || ""}
                        onChange={(e) => setForm({ ...form, conflict_note: e.target.value })} />
            </F>
          </div>
          <DialogFooter>
            <Button data-testid="nomination-save"
                    disabled={busy === "nominate" || count(form?.statement) > limit}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("nominate",
                      () => api.post(`/elections/${electionRef}/nominations`, form),
                      "The nomination is in — now gather your supporters")}>
              {busy === "nominate" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* commission review */}
      <Dialog open={Boolean(review)} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="max-w-lg" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>
              {review?.decision === "rejected" ? "Reject this nomination"
                : "Ask for more information"}
            </DialogTitle>
            <DialogDescription>
              {review?.name} and whoever nominated them will be told your reason. Nobody else on
              the roll sees it.
            </DialogDescription></DialogHeader>
          <F label="Reason" hint="At least 10 characters — it goes onto the record.">
            <Textarea rows={4} data-testid="review-reason" value={review?.reason || ""}
                      onChange={(e) => setReview({ ...review, reason: e.target.value })} />
          </F>
          <F label="Rule reference">
            <Input data-testid="review-rule" value={review?.rule_reference || ""}
                   onChange={(e) => setReview({ ...review, rule_reference: e.target.value })} />
          </F>
          <DialogFooter>
            <Button data-testid="review-save" disabled={busy === "review"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("review",
                      () => api.post(`/elections/nominations/${review.ref}/review`,
                        { decision: review.decision, reason: review.reason,
                          rule_reference: review.rule_reference }),
                      "The decision is recorded and the candidate has been told")}>
              {busy === "review" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Record it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
